// `attachments`: Swift `Attachment` (GroundTeam/Models/Activity.swift): photos, documents, business cards and audio
// on a record or activity. The file itself lives in the `company-files` bucket at
// `<company lowercase>/attachments/<fileName>` (envelope `file_path`, WEB_PLAN §0, §3.9). Decoding template
// `Attachment(fileName: "")` (GroundTeam/Sync/ResilientDecoding.swift:111).

import { ATTACHMENT_KINDS, type AttachmentKind } from '../enums';
import type { JsonObject } from '../wire';
import { newId } from '../uuid';
import {
  DATE,
  STRING,
  UUID,
  createModel,
  decodeModel,
  enumOf,
  field,
  nowWire,
  type Decoded,
  type FieldMap,
  type Known,
  type Passthrough,
} from './fields';

export interface AttachmentPayload extends Passthrough {
  id: string;
  recordId?: string;
  activityId?: string;
  kind: AttachmentKind;
  /** Object name inside `<company>/attachments/`. */
  fileName: string;
  caption: string;
  createdAt: string;
  uploadedBy?: string;
}

export const ATTACHMENT_KEYS = [
  'id',
  'recordId',
  'activityId',
  'kind',
  'fileName',
  'caption',
  'createdAt',
  'uploadedBy',
] as const satisfies readonly (keyof Known<AttachmentPayload>)[];

export const ATTACHMENT_FIELDS: FieldMap<AttachmentPayload> = {
  id: field.strict(UUID),
  recordId: field.opt(UUID),
  activityId: field.opt(UUID),
  kind: field.req(enumOf('Attachment.Kind', ATTACHMENT_KINDS), () => 'photo'),
  fileName: field.req(STRING, () => ''),
  caption: field.req(STRING, () => ''),
  createdAt: field.req(DATE, nowWire),
  uploadedBy: field.opt(UUID),
};

export function decodeAttachment(raw: JsonObject, envelopeId?: string): Decoded<AttachmentPayload> {
  return decodeModel(ATTACHMENT_FIELDS, raw, {}, envelopeId);
}

export type AttachmentInit = Partial<Known<AttachmentPayload>> & { fileName: string };

export function newAttachment(init: AttachmentInit): AttachmentPayload {
  return createModel(
    ATTACHMENT_FIELDS,
    { id: newId(), kind: 'photo', caption: '', createdAt: nowWire() },
    init,
    'attachments',
  );
}
